import { createSelector } from '@reduxjs/toolkit'

import type { QueryResultLike, QueryView } from '@krp/client-runtime'

import type { CommandFeedback } from '../../../shared/model/feedback'
import { formatKopecks } from '../../../shared/model/format'
import { AQSI_RIGHTS } from '../../../shared/model/rights'
import { formatMoment, makeQueryViewSelector, readyData } from '../../../shared/model/query'
import { sameId } from '../../../shared/api/wire'
import { makeSelectSourceCaption } from '../../source/model/selectors'
import type { AqsiSource, AqsiSourcesArgs } from '../../source/model/types'
import { buildBoard } from './board'
import type { CashierAccessState } from './cashierAccessSlice'
import { aqsiRegisterHealth } from './registerHealth'
import type { OpeningEntry } from './openingSlice'
import { blocksShiftCommands } from './operationsSlice'
import { buildOpening, buildStart, type StartPriceInput } from './start'
import type { StartMemory } from './startSlice'
import type {
  AqsiCashierRmkSettings,
  AqsiOperationStatus,
  AqsiPosCashiers,
  AqsiPriceType,
  AqsiRegisterLink,
  AqsiRegisters,
  AqsiSession,
  AqsiSessionJournal,
  FeedbackHelpVm,
  AqsiShift,
  JournalVm,
  MineVm,
  OperationVm,
  PendingOperation,
  PriceTypeOptionVm,
  PriceTypeRef,
  PriceTypesVm,
  RmkRights,
  RmkVm,
  ShiftCommandsVm,
  ShiftVm,
  Tone,
} from './types'

/**
 * Вход vm окна РМК — НЕ RootState: binding отдаёт результаты запросов и
 * намерение. Все запросы — одного rid.
 */
export interface RmkVmInput {
  resourceId: string
  sources: QueryResultLike<readonly AqsiSource[]>
  registers: QueryResultLike<AqsiRegisters>
  /**
   * Моя работа за кассой — из НАШЕЙ базы (`sessions/mine`), кабинет не нужен: идущая продажа видна, даже когда кабинет
   * не ответил на список касс.
   */
  mySession: QueryResultLike<AqsiSession | null>
  /** Журнал открытой кассы (`deviceId`); не передан — диалог закрыт. */
  journal?: { deviceId: number; result: QueryResultLike<AqsiSessionJournal> } | undefined
  /** Связь двух уровней по кассам (`deviceId`): читается ЧТЕНИЕМ, кассе ничего не отправляется. */
  links?: Readonly<Record<number, QueryResultLike<AqsiRegisterLink> | undefined>>
  shift: QueryResultLike<AqsiShift> | undefined
  operation: QueryResultLike<AqsiOperationStatus> | undefined
  pending: PendingOperation | undefined
  feedback: CommandFeedback | undefined
  /** Служебные права на этом кабинете; не передано — только кассирское. */
  rights?: RmkRights
  /** RP-FB11: виды цен общего каталога; читаются и в «Начале работы» (RP-FB22: цена выбирается до открытия смены). */
  priceTypes?: QueryResultLike<readonly AqsiPriceType[]> | undefined
  /** RP-FB22: кассиры источника (`pos/cashiers`) — для блока «Кассир» и вида входа каждого. */
  cashiers?: QueryResultLike<AqsiPosCashiers> | undefined
  /** RP-CT6: персональные настройки РМК выбранного кассира; ошибка чтения РМК не блокирует. */
  cashierRmk?: QueryResultLike<AqsiCashierRmkSettings> | undefined
  /** RP-FB22: выбор «Начала работы» этого источника (кассир, вид цены, касса) и «как в прошлый раз». */
  startMemory?: StartMemory | undefined
  /** RP-FB22: подтверждения кода, блокировки и итог последней проверки. */
  access?: CashierAccessState | undefined
  /** RP-FB22: ход «Открыть смену», пока им занят клиент. */
  opening?: OpeningEntry | undefined
}

/** Ключ «Цены кассы» в выборе вида цены: цена карточки кабинета AQSI. */
export const PRICE_TYPE_CASH = 'cash'
const CASH_PRICE_NAME = 'Цена кассы'
const CASH_PRICE_HINT = 'цена карточки в кабинете AQSI'

/** Ключ вида в выборе: `cash` или `<источник>/<ключ вида>` — ключ GUID источника косой черты не содержит. */
export /** Статус операции кабинета словами для кассира: что он означает для кассы, а не латинский код. */
function cabinetStatusWords(status: string): string {
  switch (status) {
    case 'Pending':
      return 'команда в очереди, касса её ещё не взяла (касса выключена или нет связи с ней)'
    case 'Processing':
      return 'касса выполняет команду (если просит подтверждение — подтвердите на её экране)'
    case 'Finishing':
      return 'касса выполнила команду, кабинет оформляет итог'
    case 'Completed':
      return 'команда выполнена'
    case 'Canceled':
      return 'команда отменена'
    case 'Timeout':
      return 'касса не ответила за отведённое время'
    case 'Error':
      return 'касса ответила ошибкой'
    default:
      return status
  }
}

function priceTypeId(sourceResourceId: string | null, key: string | null): string {
  return sourceResourceId === null || key === null ? PRICE_TYPE_CASH : `${sourceResourceId.toLowerCase()}/${key}`
}

function priceTypeHint(type: AqsiPriceType): string {
  if (type.sourceResourceId === null) return CASH_PRICE_HINT
  const count = type.prices ?? 0
  return `цены на ${formatMoment(type.observedAt)} · ${count} ${plural(count, 'цена', 'цены', 'цен')}`
}

function plural(count: number, one: string, few: string, many: string): string {
  const tens = count % 100
  const ones = count % 10
  if (tens >= 11 && tens <= 14) return many
  if (ones === 1) return one
  return ones >= 2 && ones <= 4 ? few : many
}

/** Виды цен каталога для «Начала работы»: список без выбранного (выбор считает `buildStart`). */
function startPriceInput(view: QueryView, data: readonly AqsiPriceType[] | undefined): StartPriceInput {
  const ready = view.status === 'ready' || view.status === 'empty'
  const options: PriceTypeOptionVm[] = (readyData(view, data) ?? []).map((type) => ({
    id: priceTypeId(type.sourceResourceId, type.key),
    name: type.name,
    hint: priceTypeHint(type),
    selected: false,
  }))
  if (!options.some((option) => option.id === PRICE_TYPE_CASH))
    options.unshift({ id: PRICE_TYPE_CASH, name: CASH_PRICE_NAME, hint: CASH_PRICE_HINT, selected: false })
  return { options, query: view, ready }
}

/** Вид цены из ключа выбора: `cash` → `null` («Цена кассы»), иначе источник и ключ вида (GUID источника косой черты не содержит). */
export function priceTypeRefOf(id: string): PriceTypeRef | null {
  const slash = id.indexOf('/')
  return id === PRICE_TYPE_CASH || slash < 0 ? null : { resourceId: id.slice(0, slash), key: id.slice(slash + 1) }
}

function priceTypesVm(view: QueryView, data: readonly AqsiPriceType[] | undefined, mine: MineVm | null): PriceTypesVm | null {
  if (mine === null) return null
  const selectedId = mine.priceTypeId
  const options: PriceTypeOptionVm[] = (readyData(view, data) ?? []).map((type) => {
    const id = priceTypeId(type.sourceResourceId, type.key)
    return { id, name: type.name, hint: priceTypeHint(type), selected: id === selectedId }
  })
  if (!options.some((option) => option.id === PRICE_TYPE_CASH))
    options.unshift({ id: PRICE_TYPE_CASH, name: CASH_PRICE_NAME, hint: CASH_PRICE_HINT, selected: selectedId === PRICE_TYPE_CASH })
  // Список не прочитан или выбранного вида в нём уже нет — выбранный всё равно виден: человек знает, по какой цене продаёт.
  if (!options.some((option) => option.selected))
    options.push({ id: selectedId, name: mine.priceTypeName, hint: 'вида нет в списке цен каталога', selected: true })
  return { query: view, options }
}

/** Ключ моей кассы из моей сессии — для подписки binding на операцию и ключ намерений. */
export function mineRegisterKey(session: AqsiSession | null | undefined): string | null {
  return session?.registerKey ?? null
}

/** Номер моей кассы (`deviceId`) из моей сессии — для подписки binding на смену и связь. */
export function mineDeviceId(session: AqsiSession | null | undefined): number | null {
  return session?.deviceId ?? null
}

const NO_START_MEMORY: StartMemory = {
  draft: { cashier: null, priceTypeId: null, deviceId: null },
  last: { cashier: null, deviceId: null, priceTypes: {} },
}

const NO_ACCESS: CashierAccessState = { proofs: {}, locks: {}, checks: {} }

/** Без прав — только кассирское: служебные права окна узнаются у Platform по ресурсу. */
export const NO_RMK_RIGHTS: RmkRights = { takeover: false, closeAny: false, journal: false }

/**
 * Аргументы «моих ресурсов» для служебных прав окна: кабинет есть в списке —
 * право на нём выдано. Постоянные объекты: один аргумент у подписки и `select`.
 */
export const RMK_RIGHT_ARGS: Readonly<Record<keyof RmkRights, AqsiSourcesArgs>> = {
  takeover: { capability: AQSI_RIGHTS.sessionTakeover },
  closeAny: { capability: AQSI_RIGHTS.sessionCloseAny },
  journal: { capability: AQSI_RIGHTS.sessionJournalRead },
}

/** Право выдано на этом кабинете. Список не прочитан — права нет: служебное не показывается наугад. */
export function hasRmkRight(sources: readonly AqsiSource[] | undefined, resourceId: string): boolean {
  return (sources ?? []).some((source) => sameId(source.id, resourceId))
}

/**
 * Отказ службы о незавершённой операции на кассе (C3b): сам текст — дословно в
 * баннере (ADR-8), здесь — что это значит и куда идти. Узнаётся по коду отказа,
 * а не по словам.
 */
function feedbackHelp(feedback: CommandFeedback | null): FeedbackHelpVm | null {
  switch (feedback?.error?.code) {
    case 'operation-unresolved':
      // Служба называет, что именно не завершено: команда смены или продажа (BF-AQSI-001). Текст про чеки — только для продаж.
      if (feedback.error?.message.includes('команда смены')) {
        return {
          text:
            'На кассе есть незавершённая команда смены. Программа проверяет её результат в кабинете. Ответ и ход проверки показаны в окне команды смены.',
          incidents: false,
        }
      }
      return {
        text:
          'Что это значит: касса не сообщила, чем кончилась последняя операция — прошла ли оплата, пробит ли чек. Пока это не выяснено, кассу не освободить и новую операцию не начать, иначе чек может пробиться дважды или не пробиться вовсе. Что сделать: откройте «Инциденты продаж» и доведите операцию до конца, или попросите об этом того, кто её начал.',
        incidents: true,
      }
    case 'receipt-pending':
      return {
        text:
          'Что это значит: покупатель заплатил, а чек ещё не пробит. Что сделать: пробейте чек — незавершённая продажа видна в «Инцидентах продаж».',
        incidents: true,
      }
    default:
      return null
  }
}

function mineOf(
  mine: AqsiSession | null | undefined,
  data: AqsiRegisters | undefined,
  links: Readonly<Record<number, AqsiRegisterLink | undefined>>,
): MineVm | null {
  if (mine === null || mine === undefined) return null
  const register = data?.registers.find((item) => item.deviceId === mine.deviceId)
  const registerName = register?.name ?? (mine.deviceSerialNumber !== '' ? `Касса ${mine.deviceSerialNumber}` : 'Касса')
  return {
    sessionId: mine.id,
    registerKey: mine.registerKey,
    registerName,
    serial: mine.deviceSerialNumber,
    openedAt: formatMoment(mine.openedAt),
    lastSeenAt: formatMoment(mine.lastSeenAt),
    shiftNumber: mine.shiftNumber,
    adoptedOutside: mine.adoptedOutside,
    version: mine.version,
    deviceId: mine.deviceId,
    cashierId: mine.cashierId ?? null,
    cashierName: mine.cashierName,
    thisTab: mine.thisTab ?? true,
    health: aqsiRegisterHealth(links[mine.deviceId], { deviceId: mine.deviceId, name: registerName }),
    priceTypeName: mine.priceTypeName ?? CASH_PRICE_NAME,
    priceTypeId: priceTypeId(mine.priceTypeResourceId, mine.priceTypeKey),
  }
}

const EMPTY_FACTS: readonly { label: string; value: string }[] = []

function shiftFacts(shift: AqsiShift): readonly { label: string; value: string }[] {
  const report = shift.report
  // Кабинет не прислал ни одной смены — что было на кассе, говорит сообщение службы над фактами («Смен на этой
  // кассе ещё не было» или «Смена №7 закрыта на кассе вне программы»). Своё «смен ещё не было» рядом с ним
  // противоречило бы известной смене №7.
  if (shift.number === null && shift.openedAt === null && shift.closedAt === null && report === null) return EMPTY_FACTS
  const number = shift.number ?? (report !== null && report.dayNumber > 0 ? report.dayNumber : null)
  const facts: { label: string; value: string }[] = [
    { label: 'Смена', value: number !== null ? `№${number}` : 'номер кабинет не сообщил' },
    { label: 'Открыта', value: formatMoment(shift.openedAt) },
  ]
  if (shift.closedAt !== null) facts.push({ label: 'Закрыта', value: formatMoment(shift.closedAt) })
  if (report !== null) {
    facts.push(
      { label: 'Кассир', value: report.cashierName || '—' },
      { label: 'Чеков', value: String(report.chequesCount) },
      { label: 'Приход', value: formatKopecks(report.incomeTotalKopecks) },
      {
        label: 'Наличными / безналом',
        value: `${formatKopecks(report.incomeCashKopecks)} / ${formatKopecks(report.incomeCashlessKopecks)}`,
      },
      { label: 'Возвраты', value: formatKopecks(report.refundTotalKopecks) },
      { label: 'Наличных в кассе', value: formatKopecks(report.cashInCashBoxKopecks) },
      { label: 'Непереданных в ОФД', value: String(report.unsentDocsCount) },
      { label: 'Дней ФН осталось', value: String(report.fsDaysRemaining) },
    )
  }
  return facts
}

const EMPTY_PROBLEMS: readonly string[] = []

function shiftVm(view: QueryView | null, shift: AqsiShift | undefined): ShiftVm | null {
  if (view === null) return null
  const ready = readyData(view, shift)
  return {
    query: view,
    isOpen: ready?.isOpen ?? null,
    number: ready?.number ?? null,
    message: ready?.message ?? '',
    facts: ready !== undefined ? shiftFacts(ready) : [],
    problems: ready?.report?.problems ?? EMPTY_PROBLEMS,
    openedAt: ready?.isOpen === true ? ready.openedAt : null,
  }
}

function shiftCommands(
  mine: MineVm | null,
  shift: ShiftVm | null,
  pending: PendingOperation | undefined,
  busy: boolean,
): ShiftCommandsVm {
  const none = (blockedReason: string): ShiftCommandsVm => ({
    canOpen: false,
    canClose: false,
    canAdopt: false,
    blockedReason,
  })
  if (mine === null) return none('Начните работу за кассой, чтобы управлять сменой')
  if (blocksShiftCommands(pending)) {
    return none(
      'Прошлая команда смены не завершена или её исход неизвестен — сначала проверьте статус. Повторять команду нельзя.',
    )
  }
  if (busy) return none('Команда выполняется')
  if (shift === null || shift.isOpen === null) return none('Состояние смены ещё не прочитано у кабинета')
  if (!shift.isOpen) return { canOpen: true, canClose: false, canAdopt: false, blockedReason: null }
  if (mine.shiftNumber !== null && mine.shiftNumber === shift.number) {
    return { canOpen: false, canClose: true, canAdopt: false, blockedReason: null }
  }
  return {
    canOpen: false,
    canClose: false,
    canAdopt: true,
    blockedReason: 'Смена открыта не в этой работе — примите её, чтобы продолжить или закрыть',
  }
}

const STATE_TEXT: Readonly<Record<AqsiOperationStatus['state'], { text: string; tone: Tone }>> = {
  Pending: { text: 'Ожидаем подтверждение кассы', tone: 'neutral' },
  Sent: { text: 'Ожидаем подтверждение кассы', tone: 'neutral' },
  Succeeded: { text: 'Выполнена — подтверждено фактом смены', tone: 'success' },
  Failed: { text: 'Не выполнена', tone: 'danger' },
  Unknown: { text: 'Исход неизвестен', tone: 'warning' },
}

function operationVm(
  pending: PendingOperation | undefined,
  view: QueryView | null,
  status: AqsiOperationStatus | undefined,
  checking: boolean,
  reconciling: boolean,
): OperationVm | null {
  if (pending === undefined) return null
  const kindText =
    pending.kind === 'open' ? 'Открытие смены' : pending.kind === 'close' ? 'Закрытие смены' : 'Команда смены'
  const base = {
    operationId: pending.operationId,
    registerKey: pending.registerKey,
    startedAt: pending.startedAt,
    version: null as number | null,
    kindText,
    kind: pending.kind,
    phase: pending.phase,
    canReconcile: false,
    manualRequired: false,
    checking,
    reconciling,
  }
  if (pending.phase === 'sending') {
    return {
      ...base,
      stateText: 'Команда отправляется…',
      tone: 'neutral',
      details: [],
      canCheck: false,
      canDismiss: false,
    }
  }
  if (pending.phase === 'not-found') {
    return {
      ...base,
      stateText:
        'Служба так и не узнала эту команду — до неё она не дошла. Прочитайте смену: если она не изменилась, можно отдать команду заново.',
      tone: 'neutral',
      details: [],
      canCheck: false,
      canDismiss: true,
    }
  }
  const current = view !== null ? readyData(view, status) : undefined
  if (current === undefined || current.operationId !== pending.operationId) {
    const error = view?.error ?? null
    return {
      ...base,
      stateText:
        pending.phase === 'unknown'
          ? 'Ответ не получен: неизвестно, выполнена ли команда. Повтор запрещён — проверьте статус (служба могла ещё не записать команду).'
          : 'Статус команды читается…',
      tone: 'warning',
      details: error !== null ? [error.message] : [],
      canCheck: true,
      canDismiss: false,
    }
  }
  const state = STATE_TEXT[current.state]
  const kindKnown =
    current.kind === 'aqsi.shift.open'
      ? 'Открытие смены'
      : current.kind === 'aqsi.shift.close'
        ? 'Закрытие смены'
        : kindText
  const details = [
    current.providerStatus !== null ? `Кабинет: ${cabinetStatusWords(current.providerStatus)}` : null,
    current.providerMessage,
    current.note,
    current.sentAt !== null ? `Отправлена: ${formatMoment(current.sentAt)}` : null,
    current.checkedAt !== null ? `Проверена: ${formatMoment(current.checkedAt)}` : null,
  ].filter((line): line is string => line !== null && line !== '')
  const unresolved = !isSettled(current.state)
  const manualRequired = unresolved && current.nextAction === 'ManualReconciliation'
  const providerError = unresolved && ['Error', 'Timeout'].includes(current.providerStatus ?? '')
  return {
    ...base,
    version: current.version,
    kindText: kindKnown,
    stateText: manualRequired ? 'Исход не подтверждён — требуется сверка' : providerError ? 'Кабинет сообщил об ошибке — проверяем результат' : state.text,
    tone: manualRequired || providerError ? 'warning' : state.tone,
    details,
    canCheck: unresolved,
    // Статус (GET) только читает журнал службы; продвинуть Pending/Sent/Unknown к итогу может лишь сверка.
    canReconcile: unresolved && current.nextAction !== 'None',
    manualRequired,
    canDismiss: !unresolved,
  }
}

function isSettled(state: AqsiOperationStatus['state']): boolean {
  return state === 'Succeeded' || state === 'Failed'
}

/** Что делает окно прямо сейчас с кассой (открытие работы, проверка связи): слово для строки состояния. */
function workingOf(feedback: CommandFeedback | null): RmkVm['working'] {
  if (feedback === null || feedback.phase !== 'sending') return null
  if (!feedback.command.startsWith('register.') && feedback.command !== 'link.check') return null
  const deviceId = Number(feedback.target)
  return Number.isInteger(deviceId) ? { deviceId, text: feedback.note ?? 'Выполняется…' } : null
}

const NO_LINKS: NonNullable<RmkVmInput['links']> = {}

function journalVm(input: RmkVmInput['journal'], view: QueryView | null): JournalVm | null {
  if (input === undefined || view === null) return null
  const data = readyData(view, input.result.data)
  return {
    deviceId: input.deviceId,
    query: view,
    events: (data?.events ?? []).map((event) => ({
      at: event.at,
      atText: formatMoment(event.at),
      title: event.title,
      actor: event.actorDisplayName,
      note: event.note,
    })),
  }
}

/** Фабрика: один экземпляр на смонтированный binding (D07). */
export const makeSelectRmkVm = () => {
  const selectSource = makeSelectSourceCaption()
  const selectRegistersView = makeQueryViewSelector(
    (input: RmkVmInput) => input.registers,
    () => false,
  )
  const selectMineView = makeQueryViewSelector(
    (input: RmkVmInput) => input.mySession,
    () => false,
  )
  const selectShiftView = makeQueryViewSelector(
    (input: RmkVmInput) => input.shift,
    () => false,
  )
  const selectOperationView = makeQueryViewSelector(
    (input: RmkVmInput) => input.operation,
    () => false,
  )
  const selectJournalView = makeQueryViewSelector(
    (input: RmkVmInput) => input.journal?.result,
    () => false,
  )
  const selectPriceTypesView = makeQueryViewSelector(
    (input: RmkVmInput) => input.priceTypes,
    (data) => data.length === 0,
  )
  const selectSourceVm = (input: RmkVmInput) =>
    selectSource({ purpose: 'rmk', resourceId: input.resourceId, query: input.sources })
  const selectRights = (input: RmkVmInput) => input.rights ?? NO_RMK_RIGHTS
  const selectBusy = (input: RmkVmInput) => input.feedback?.phase === 'sending' || input.pending?.phase === 'sending'
  const selectLinks = (input: RmkVmInput) => input.links ?? NO_LINKS
  // Данные связи — по полям результата: новый объект `select` не должен пересобирать доску на каждое действие стора.
  const selectLinkData = createSelector([selectLinks], (links) => {
    const data: Record<number, AqsiRegisterLink | undefined> = {}
    for (const [deviceId, result] of Object.entries(links)) data[Number(deviceId)] = result?.data
    return data
  })

  const selectBoard = createSelector(
    [selectRegistersView, (input: RmkVmInput) => input.registers.data, selectLinkData, selectRights, selectBusy],
    (view, data, links, rights, busy) => buildBoard(view, data, links, rights, busy),
  )
  const selectJournal = createSelector(
    [(input: RmkVmInput) => input.journal, (input: RmkVmInput) => (input.journal === undefined ? null : selectJournalView(input))],
    journalVm,
  )
  const selectFeedbackHelp = createSelector([(input: RmkVmInput) => input.feedback ?? null], feedbackHelp)
  const selectMine = createSelector(
    [
      selectMineView,
      (input: RmkVmInput) => input.mySession.data,
      selectRegistersView,
      (input: RmkVmInput) => input.registers.data,
      selectLinkData,
    ],
    (mineView, session, registersView, data, links) => mineOf(readyData(mineView, session), readyData(registersView, data), links),
  )
  const selectShift = createSelector(
    [
      (input: RmkVmInput) => (input.shift === undefined ? null : selectShiftView(input)),
      (input: RmkVmInput) => input.shift?.data,
    ],
    shiftVm,
  )
  const selectOperation = createSelector(
    [
      (input: RmkVmInput) => input.pending,
      (input: RmkVmInput) => (input.operation === undefined ? null : selectOperationView(input)),
      (input: RmkVmInput) => input.operation?.data,
      (input: RmkVmInput) => (input.operation?.isLoading ?? false),
      (input: RmkVmInput) => input.feedback?.command === 'operation.reconcile' && input.feedback.phase === 'sending',
    ],
    operationVm,
  )
  const selectPriceTypes = createSelector(
    [selectPriceTypesView, (input: RmkVmInput) => input.priceTypes?.data, selectMine],
    priceTypesVm,
  )
  const selectStartPrice = createSelector(
    [selectPriceTypesView, (input: RmkVmInput) => input.priceTypes?.data],
    startPriceInput,
  )
  const selectCashiersView = makeQueryViewSelector(
    (input: RmkVmInput) => input.cashiers,
    (data) => data.cashiers.length === 0,
  )
  const selectStart = createSelector(
    [
      (input: RmkVmInput) => input.resourceId,
      selectCashiersView,
      (input: RmkVmInput) => input.cashiers?.data,
      (input: RmkVmInput) => input.startMemory ?? NO_START_MEMORY,
      (input: RmkVmInput) => input.access ?? NO_ACCESS,
      selectStartPrice,
      selectRegistersView,
      selectBoard,
      selectBusy,
      (input: RmkVmInput) => input.cashierRmk?.data,
    ],
    (resourceId, cashiersView, cashiers, memory, access, price, registersQuery, board, busy, rmk) =>
      buildStart({ resourceId, cashiersView, cashiers, memory, access, price, registersQuery, rows: board.rows, busy, rmk }),
  )
  const selectOpening = createSelector(
    [(input: RmkVmInput) => input.opening, selectMine, selectOperation, selectShift],
    (entry, mine, operation, shift) => buildOpening(entry, mine, operation, shift),
  )

  return createSelector(
    [
      (input: RmkVmInput) => input.resourceId,
      selectSourceVm,
      selectRegistersView,
      selectMineView,
      selectBoard,
      selectRights,
      selectFeedbackHelp,
      selectMine,
      selectBusy,
      selectShift,
      selectOperation,
      selectJournal,
      (input: RmkVmInput) => input.pending,
      (input: RmkVmInput) => input.feedback ?? null,
      selectPriceTypes,
      selectStart,
      selectOpening,
    ],
    (
      resourceId,
      source,
      registersQuery,
      mineQuery,
      board,
      rights,
      help,
      mine,
      busy,
      shift,
      operation,
      journal,
      pending,
      feedback,
      priceTypes,
      start,
      opening,
    ): RmkVm => ({
      resourceId,
      source,
      registersQuery,
      mineQuery,
      board,
      rights,
      feedbackHelp: help,
      mine,
      busy,
      working: workingOf(feedback),
      journal,
      shift: mine === null ? null : shift,
      shiftCommands: shiftCommands(mine, mine === null ? null : shift, pending, busy),
      operation,
      feedback,
      priceTypes,
      start,
      opening,
    }),
  )
}
